/**
 * Das Menü „⋯" einer Notiz-Karte — alles Seltene (Benjamin, 22.09.2026). Aus
 * `NotizKarte` herausgelöst (13.10.2026), damit die Karte lesbar bleibt. Das offene
 * Menü trägt `is-offen` und liegt damit über den Karten darunter.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useNotizAendern, useNotizLoeschen, usePersonen } from "../../hooks/useNotizen";
import { vorname, type Notiz } from "../../lib/notizen";

type Props = {
  notiz: Notiz;
  aendern: ReturnType<typeof useNotizAendern>;
  loeschen: ReturnType<typeof useNotizLoeschen>;
  onTextAendern: () => void;
  punkteBearbeiten: boolean;
  onPunkteBearbeiten: () => void;
  /** „Kundendaten" — nur, wenn der Kundenname nicht schon im Titel klickbar ist. */
  onKunde?: () => void;
  kundeLink?: string;
  projektLink?: string;
  /** „Termin im Planer anlegen" — wenn der Planer-Termin kein eigener Knopf ist. */
  terminLink?: string;
  /** „Zurückstellen …" — wenn es dafür keinen „Termin"-Knopf gibt. */
  onZurueckstellen?: () => void;
  /** „Nicht um Bewertung bitten". */
  onVerzichten?: () => void;
};

export default function NotizMenue(p: Props) {
  const { notiz, aendern, loeschen } = p;
  const personen = usePersonen().data || [];
  const [offen, setOffen] = useState(false);
  const [scharf, setScharf] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) { setOffen(false); setScharf(false); } };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [offen]);

  /** Ein Eintrag, der das Menü danach schließt. */
  const und = (f: () => void) => () => { f(); setOffen(false); };

  return (
    <div className={"nz-menue" + (offen ? " is-offen" : "")} ref={ref}>
      <button type="button" className="nz-menue-knopf" aria-label="Weitere Aktionen" aria-expanded={offen} onClick={() => setOffen((v) => !v)}>⋯</button>
      {offen && (
        <div className="nz-menue-liste" role="menu">
          {personen.length > 1 && personen.filter((x) => x.id !== notiz.zustaendig.id).map((x) => (
            <button key={x.id} type="button" role="menuitem" onClick={und(() => aendern.mutate({ id: notiz.id, zustaendig_id: x.id }))}>
              {x.ich ? "Mir zuweisen" : `An ${vorname(x.name)} geben`}
            </button>
          ))}
          <button type="button" role="menuitem" onClick={und(p.onTextAendern)}>{notiz.text ? "Text ändern" : "Text dazu"}</button>
          {notiz.punkte.length > 0 && (
            <button type="button" role="menuitem" className="nz-nur-touch" onClick={und(p.onPunkteBearbeiten)}>
              {p.punkteBearbeiten ? "Punkte fertig" : "Punkte bearbeiten"}
            </button>
          )}
          {p.onKunde && <button type="button" role="menuitem" onClick={und(p.onKunde)}>Kundendaten</button>}
          {!p.onKunde && p.kundeLink && <Link role="menuitem" to={p.kundeLink}>Zum Kunden</Link>}
          {p.projektLink && <Link role="menuitem" to={p.projektLink}>Zum Projekt</Link>}
          {p.terminLink && <Link role="menuitem" to={p.terminLink}>Termin im Planer anlegen</Link>}
          {p.onVerzichten && <button type="button" role="menuitem" onClick={und(p.onVerzichten)}>Nicht um Bewertung bitten</button>}
          {p.onZurueckstellen && <button type="button" role="menuitem" onClick={und(p.onZurueckstellen)}>Zurückstellen …</button>}
          <button type="button" role="menuitem" onClick={und(() => aendern.mutate({ id: notiz.id, abgelegt: !notiz.abgelegt_am }))}>
            {notiz.abgelegt_am ? "Zurückholen" : "Ablegen"}
          </button>
          {scharf ? (
            <button type="button" role="menuitem" className="is-rot" onClick={() => loeschen.mutate(notiz.id)}>Wirklich löschen</button>
          ) : (
            <button type="button" role="menuitem" onClick={() => setScharf(true)}>Löschen …</button>
          )}
        </div>
      )}
    </div>
  );
}
